<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-list-ol me-2" style="color: #64748b;"></i>{{ $t('Tax Invoice Register') }}
          </h5>
          <small class="text-muted">{{ $t('Audit tax invoice number sequence per branch / year') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-success btn-sm" @click="exportExcel" :disabled="loading || !filters.branch || !invoices.length">
          <i class="fa-solid fa-file-excel me-1"></i>{{ $t('Export Excel') }}
        </button>
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading || !filters.branch">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading || !filters.branch">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 160px; max-width: 220px;" :disabled="loading">
            <option value="" disabled>{{ $t('Select branch') + '...' }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <select v-model.number="filters.year" class="form-select form-select-sm" style="min-width: 110px; max-width: 140px;" :disabled="loading">
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <!-- No branch selected -->
    <div v-if="!filters.branch" class="text-center text-muted py-5">
      <i class="fa-solid fa-store fa-2x mb-3 d-block"></i>
      {{ $t('Please select a branch to view the register.') }}
    </div>

    <div v-else :class="{ 'report-loading': loading }">

    <!-- Integrity alert -->
    <div
      v-if="toNumber(summary.missing_count) > 0"
      class="alert alert-danger d-flex align-items-center"
      role="alert"
    >
      <i class="fa-solid fa-triangle-exclamation fa-lg me-2"></i>
      <div>
        <strong>{{ $t('Sequence gap detected!') }}</strong>
        {{ $t('There are missing tax invoice numbers — this requires investigation.') }}
        ({{ $t('Missing') }}: {{ summary.missing_count }})
      </div>
    </div>
    <div
      v-else
      class="alert alert-success d-flex align-items-center"
      role="alert"
    >
      <i class="fa-solid fa-circle-check fa-lg me-2"></i>
      <div>{{ $t('Sequence is complete — no missing numbers.') }}</div>
    </div>

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Expected') }}</div>
            <div class="fw-bold fs-4">{{ summary.expected_count || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Actual') }}</div>
            <div class="fw-bold fs-4">{{ summary.actual_count || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Missing') }}</div>
            <div class="fw-bold fs-4" :class="toNumber(summary.missing_count) > 0 ? 'text-danger' : 'text-success'">
              {{ summary.missing_count || 0 }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Active') }}</div>
            <div class="fw-bold fs-4 text-success">{{ summary.active_count || 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Voided') }}</div>
            <div class="fw-bold fs-4 text-danger">{{ summary.voided_count || 0 }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Missing sequences -->
    <div v-if="missingSequences.length" class="card border-danger shadow-sm mb-4">
      <div class="card-header bg-danger-subtle text-danger-emphasis fw-bold">
        <i class="fa-solid fa-link-slash me-2"></i>{{ $t('Missing Sequences') }}
      </div>
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2">
          <span
            v-for="m in missingSequences"
            :key="m.seq_no"
            class="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle p-2"
          >
            #{{ m.seq_no }} — {{ m.expected_invoice_no }}
          </span>
        </div>
      </div>
    </div>

<!--
      ลำดับใบกำกับภาษีอย่างย่อ — คนละชุดเลขกับใบกำกับเต็มรูปข้างบน counter รีเซ็ตรายเดือน
      เลขออกตอน "ปิดการขาย" (D9) บิลที่ไม่จ่าย/ถูกยกเลิก/ถูกรวม จึงไม่กินเลข → ปกติต้องไม่มีเลขขาดเลย
      ถ้ามีขึ้นมาคือของผิดปกติที่ต้องไปตามดู ไม่ใช่เรื่องธรรมดาแบบสมัยที่เลข = bill_no
      ซ่อนทั้งบล็อกเมื่อสาขาไม่ได้ออกใบกำกับอย่างย่อในปีนั้น (applicable = false)
    -->
    <div v-if="abbrSummary.applicable" class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-light">
        <span class="fw-bold"><i class="fa-solid fa-receipt me-2"></i>{{ $t('Abbreviated Tax Invoice Sequence') }}</span>
        <small class="text-muted ms-2">{{ $t('Receipt numbers issued as abbreviated tax invoices') }}</small>
      </div>
      <div class="card-body">
        <div class="row g-3 mb-3">
          <div class="col-4">
            <div class="text-muted small">{{ $t('Expected') }}</div>
            <div class="fw-bold fs-5">{{ abbrSummary.expected_count || 0 }}</div>
          </div>
          <div class="col-4">
            <div class="text-muted small">{{ $t('Actual') }}</div>
            <div class="fw-bold fs-5 text-success">{{ abbrSummary.actual_count || 0 }}</div>
          </div>
          <div class="col-4">
            <div class="text-muted small">{{ $t('Missing') }}</div>
            <div class="fw-bold fs-5" :class="toNumber(abbrSummary.missing_count) > 0 ? 'text-warning' : 'text-success'">
              {{ abbrSummary.missing_count || 0 }}
            </div>
          </div>
        </div>

        <div v-if="!abbrMissing.length" class="text-success small">
          <i class="fa-solid fa-circle-check me-1"></i>{{ $t('No missing numbers') }}
        </div>

        <template v-else>
          <div class="alert alert-warning py-2 small mb-3">
            <i class="fa-solid fa-circle-info me-1"></i>
            {{ $t('Numbers are issued at checkout so gaps should not happen — check each one below.') }}
          </div>
          <div class="table-responsive">
            <table class="table table-sm mb-0 align-middle">
              <thead class="table-light">
                <tr>
                  <th class="ps-3">{{ $t('Month') }}</th>
                  <th>{{ $t('Number') }}</th>
                  <th>{{ $t('Reason') }}</th>
                  <th class="pe-3">{{ $t('Detail') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in abbrMissing" :key="m.month + '-' + m.seq_no">
                  <td class="ps-3 text-nowrap">{{ m.month }}</td>
                  <td class="fw-semibold">{{ m.expected_invoice_no }}</td>
                  <td>
                    <span class="badge" :class="reasonBadgeClass(m.reason)">{{ $t(reasonLabel(m.reason)) }}</span>
                  </td>
                  <td class="pe-3 text-muted small">{{ m.detail || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="abbrSummary.truncated" class="text-muted small mt-2">
            {{ $t('Showing the first entries only — export to Excel for the full list.') }}
          </div>
        </template>
      </div>
    </div>

    <!-- Invoice Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3 text-end" style="width: 60px;">{{ $t('Seq') }}</th>
                <th>{{ $t('Invoice No.') }}</th>
                <th>{{ $t('Bill No.') }}</th>
                <th>{{ $t('Issued At') }}</th>
                <th>{{ $t('Customer') }}</th>
                <th class="text-end">{{ $t('Total Amount') }}</th>
                <th class="text-center pe-3">{{ $t('Status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!invoices.length && !loading">
                <td colspan="7" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr
                v-for="row in invoices"
                :key="row.seq_no"
                :class="{ 'table-danger-subtle text-decoration-line-through text-muted': row.is_voided }"
              >
                <td class="ps-3 text-end">{{ row.seq_no }}</td>
                <td class="fw-semibold">{{ row.invoice_no }}</td>
                <td>{{ row.bill_no }}</td>
                <td class="text-nowrap">{{ row.issued_at }}</td>
                <td>
                  <div>{{ row.customer_name || '-' }}</div>
                  <div v-if="row.customer_tax_id" class="text-muted small">{{ row.customer_tax_id }}</div>
                </td>
                <td class="text-end fw-semibold">{{ formatCurrency(row.total_amount) }}</td>
                <td class="text-center pe-3">
                  <span
                    v-if="row.is_voided"
                    class="badge bg-danger text-decoration-none d-inline-block"
                    :title="voidTooltip(row)"
                  >
                    {{ $t('Voided') }}
                  </span>
                  <span v-else class="badge bg-success">{{ $t('Active') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Reports } from '@/services/endpoint/reports.service';
import { Branches } from '@/services/endpoint/branches.service';
import { exportRowsToXlsx, MONEY_FMT } from '@/utils/excelExport';
import reportMixin from './reportMixin';

const EMPTY_SUMMARY = {
  branch_id: null, branch_code: '', year: null,
  expected_count: 0, actual_count: 0, missing_count: 0,
  active_count: 0, voided_count: 0,
};

export default {
  name: 'ReportTaxInvoiceRegister',
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Tax Invoice Register',
      filters: {
        branch: '',
        year: new Date().getFullYear(),
      },
      summary: { ...EMPTY_SUMMARY },
      invoices: [],
      missingSequences: [],
      // ลำดับใบกำกับอย่างย่อ (bill_no) — คนละชุดกับใบกำกับเต็มรูป ดู vat-compliance.md D1
      abbreviated: { summary: {}, missing_sequences: [] },
    };
  },
  computed: {
    yearOptions() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current; y >= 2020; y--) years.push(y);
      return years;
    },
    abbrSummary() {
      return this.abbreviated.summary || {};
    },
    abbrMissing() {
      return this.abbreviated.missing_sequences || [];
    },
  },
  methods: {
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    resetData() {
      this.summary = { ...EMPTY_SUMMARY };
      this.invoices = [];
      this.missingSequences = [];
      this.abbreviated = { summary: {}, missing_sequences: [] };
    },
    async loadBranches() {
      // Seed the branch dropdown. The register endpoint requires a branch_id, so
      // it can't be called to discover branches first. Fall back silently if the
      // user lacks branch_view — filter.branches from the report refreshes it later.
      try {
        const res = await Branches.list('?limit=500');
        const list = (res?.data?.data || []).map(b => ({ id: b.id, name: b.name }));
        this.branches = list;
        if (!this.filters.branch && list.length) {
          this.filters.branch = list[0].id; // triggers the branch watcher → loadReport
        }
      } catch (e) {
        // ignore — page shows the "select a branch" prompt with an empty list
      }
    },
    async fetchReport() {
      if (!this.filters.branch) {
        this.resetData();
        return;
      }
      const res = await Reports.taxInvoiceRegister({
        branch_id: this.filters.branch,
        year: this.filters.year,
      });
      const payload = res?.data?.data || {};
      const filter = res?.data?.filter || {};
      if (filter.branches && filter.branches.length) this.branches = filter.branches;
      this.summary = { ...EMPTY_SUMMARY, ...(payload.summary || {}) };
      this.invoices = payload.invoices || [];
      this.missingSequences = payload.missing_sequences || [];
      this.abbreviated = payload.abbreviated || { summary: {}, missing_sequences: [] };
    },

    // สาเหตุที่เลขถูกใช้ไปโดยไม่มีใบกำกับอย่างย่อ — คีย์มาจาก API (ReportController::abbreviatedInvoiceAudit)
    reasonLabel(code) {
      return {
        voided: 'Bill voided',
        removed: 'Merged into another bill or removed',
        full_tax_invoice: 'Issued as a full tax invoice',
        not_a_sale: 'Number issued but the bill is no longer a sale',
        not_found: 'Bill record not found',
      }[code] || code;
    },

    reasonBadgeClass(code) {
      // 2 ตัวนี้ไม่ควรเกิดเลยหลัง D9 (เลขออกตอนปิดการขาย) — ถ้าโผล่คือต้องไปตามดู
      return ['not_found', 'not_a_sale'].includes(code) ? 'bg-danger' : 'bg-secondary';
    },

    voidTooltip(row) {
      const parts = [];
      if (row.voided_at) parts.push(this.$t('Voided At') + ': ' + row.voided_at);
      if (row.voided_by) parts.push(this.$t('Voided By') + ': ' + row.voided_by);
      if (row.void_reason) parts.push(this.$t('Reason') + ': ' + row.void_reason);
      return parts.join('\n');
    },

    exportExcel() {
      const columns = [
        { header: this.$t('Seq'), key: 'seq_no', width: 8 },
        { header: this.$t('Invoice No.'), key: 'invoice_no', width: 24 },
        { header: this.$t('Bill No.'), key: 'bill_no', width: 22 },
        { header: this.$t('Issued At'), key: 'issued_at', width: 18 },
        { header: this.$t('Customer'), key: 'customer_name', width: 28 },
        { header: this.$t('Customer Tax ID'), key: 'customer_tax_id', width: 18 },
        { header: this.$t('Total Amount'), value: r => this.toNumber(r.total_amount), width: 16, numFmt: MONEY_FMT },
        { header: this.$t('Status'), value: r => (r.is_voided ? this.$t('Voided') : this.$t('Active')), width: 12 },
        { header: this.$t('Reason'), value: r => r.void_reason || '', width: 24 },
      ];
      const preRows = [
        [this.$t('Tax Invoice Register')],
        [this.selectedBranchLabel + '  |  ' + this.$t('Year') + ' ' + this.filters.year],
        [
          this.$t('Expected') + ': ' + (this.summary.expected_count || 0),
          this.$t('Actual') + ': ' + (this.summary.actual_count || 0),
          this.$t('Missing') + ': ' + (this.summary.missing_count || 0),
          this.$t('Active') + ': ' + (this.summary.active_count || 0),
          this.$t('Voided') + ': ' + (this.summary.voided_count || 0),
        ],
        [],
      ];
      const postRows = [];
      if (this.missingSequences.length) {
        postRows.push([]);
        postRows.push([this.$t('Missing Sequences')]);
        this.missingSequences.forEach(m => {
          postRows.push(['#' + m.seq_no, m.expected_invoice_no]);
        });
      }
      // ลำดับใบกำกับอย่างย่อ — บัญชีต้องใช้ไฟล์นี้อธิบายเลขที่ขาด ต้องติดไปกับ export ด้วย
      if (this.abbrSummary.applicable) {
        postRows.push([]);
        postRows.push([this.$t('Abbreviated Tax Invoice Sequence')]);
        postRows.push([
          this.$t('Expected') + ': ' + (this.abbrSummary.expected_count || 0),
          this.$t('Actual') + ': ' + (this.abbrSummary.actual_count || 0),
          this.$t('Missing') + ': ' + (this.abbrSummary.missing_count || 0),
        ]);
        if (this.abbrMissing.length) {
          postRows.push([this.$t('Month'), this.$t('Number'), this.$t('Reason'), this.$t('Detail')]);
          this.abbrMissing.forEach(m => {
            postRows.push([m.month, m.expected_invoice_no, this.$t(this.reasonLabel(m.reason)), m.detail || '']);
          });
        }
      }
      const branchTag = this.summary.branch_code || this.filters.branch;
      exportRowsToXlsx({
        filename: `tax-invoice-register-${branchTag}-${this.filters.year}.xlsx`,
        sheetName: 'Register',
        columns,
        rows: this.invoices,
        preRows,
        postRows,
      });
    },

    // Register uses branch + year (no date range), so override the mixin header.
    buildPdfHeader() {
      return [
        { text: `Siva — ${this.$t(this.reportLabel)}`, style: 'reportTitle' },
        { text: `${this.selectedBranchLabel}  |  ${this.$t('Year')} ${this.filters.year}`, style: 'reportSubtitle' },
      ];
    },

    async buildPdfDefinition() {
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Tax Invoice Register'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('Expected') + '\n', style: 'kpiLabel' }, { text: String(this.summary.expected_count || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Actual') + '\n', style: 'kpiLabel' }, { text: String(this.summary.actual_count || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Missing') + '\n', style: 'kpiLabel' }, { text: String(this.summary.missing_count || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Active') + '\n', style: 'kpiLabel' }, { text: String(this.summary.active_count || 0), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Voided') + '\n', style: 'kpiLabel' }, { text: String(this.summary.voided_count || 0), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 12],
        },
      ];

      if (this.missingSequences.length) {
        content.push({
          text: this.$t('Missing Sequences') + ': ' + this.missingSequences.map(m => m.expected_invoice_no).join(', '),
          color: '#b91c1c',
          margin: [0, 0, 0, 12],
        });
      }

      content.push({
        table: {
          headerRows: 1,
          widths: [28, 'auto', 'auto', 'auto', '*', 'auto', 'auto'],
          body: [
            [
              { text: this.$t('Seq'), style: 'tableHeader', alignment: 'right' },
              { text: this.$t('Invoice No.'), style: 'tableHeader' },
              { text: this.$t('Bill No.'), style: 'tableHeader' },
              { text: this.$t('Issued At'), style: 'tableHeader' },
              { text: this.$t('Customer'), style: 'tableHeader' },
              { text: this.$t('Total Amount'), style: 'tableHeader', alignment: 'right' },
              { text: this.$t('Status'), style: 'tableHeader' },
            ],
            ...this.invoices.map(r => {
              const cell = (text, extra = {}) => ({
                text,
                style: 'tableCell',
                color: r.is_voided ? '#b91c1c' : undefined,
                decoration: r.is_voided ? 'lineThrough' : undefined,
                ...extra,
              });
              return [
                cell(String(r.seq_no), { alignment: 'right' }),
                cell(r.invoice_no),
                cell(r.bill_no),
                cell(r.issued_at),
                cell(r.customer_name || '-'),
                cell(this.formatCurrencyRaw(r.total_amount), { alignment: 'right' }),
                cell(r.is_voided ? this.$t('Voided') : this.$t('Active')),
              ];
            }),
          ],
        },
        layout: 'lightHorizontalLines',
      });

      return { content };
    },
  },
  watch: {
    'filters.year'() {
      this.loadReport();
    },
  },
  mounted() {
    this.loadBranches();
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
.table-danger-subtle {
  background-color: #fdf0f0;
}
</style>
